import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, AlertTriangle, HelpCircle, FileVideo, Clock,
  ScanEye, ExternalLink, Filter, Link2
} from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_API_URL}/api`;

const VERDICT_META = {
  REAL:      { color: "text-verdict-real",      border: "border-verdict-real/30",      bg: "bg-verdict-real/10",      icon: ShieldCheck },
  FAKE:      { color: "text-verdict-fake",      border: "border-verdict-fake/30",      bg: "bg-verdict-fake/10",      icon: AlertTriangle },
  SUSPICIOUS:{ color: "text-verdict-uncertain",  border: "border-verdict-uncertain/30", bg: "bg-verdict-uncertain/10", icon: AlertTriangle },
  UNCERTAIN: { color: "text-verdict-uncertain", border: "border-verdict-uncertain/30", bg: "bg-verdict-uncertain/10", icon: HelpCircle },
};

// ── Display label: risk-based ────────────────────────────────────────────
function getDisplayLabel(verdict, confidence, t) {
  const v = (verdict || "UNCERTAIN").toUpperCase();
  const c = parseFloat(confidence) || 0;
  if (c < 60) return t("results.displayInsufficient"); // < 60% — insufficient
  if (c < 80) return t("results.displayUncertain");    // 60–79% — uncertain regardless
  if (v === "FAKE") return t("results.displayFake");   // ≥ 80% FAKE → HIGH RISK
  if (v === "REAL") return t("results.displayReal");   // ≥ 80% REAL → LOW RISK
  return t("results.displayUncertain");                // SUSPICIOUS / UNCERTAIN
}

const SORTS = [
  { key: "newest",     labelKey: "videos.sort.newest"    },
  { key: "trending",   labelKey: "videos.sort.trending"  },
  { key: "most_views", labelKey: "videos.sort.mostViews" },
  { key: "popular",    labelKey: "videos.sort.popular"   },
];

const FILTERS = [
  { key: null,        labelKey: "videos.filter.all"       },
  { key: "FAKE",      labelKey: "videos.filter.deepfake"  },
  { key: "REAL",      labelKey: "videos.filter.real"      },
  { key: "UNCERTAIN", labelKey: "videos.filter.uncertain" },
];

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } };
const item = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

export default function Videos() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [pages, setPages]       = useState(1);
  const [total, setTotal]       = useState(0);
  const [verdict, setVerdict]   = useState(null);
  const [sort, setSort]         = useState("newest");

  const fetchData = useCallback(async (p = 1, v = null, s = "newest") => {
    setLoading(true);
    try {
      const lang = i18n.language?.split("-")[0] || "id";
      const params = { page: p, limit: 12, sort: s, lang };
      if (v) params.verdict = v;
      const res = await axios.get(`${API}/public/analyses`, { params });
      setAnalyses(res.data.analyses || []);
      setPage(res.data.page);
      setPages(res.data.pages);
      setTotal(res.data.total);
    } catch {
      setAnalyses([]);
    } finally {
      setLoading(false);
    }
  }, [i18n.language]);

  useEffect(() => { fetchData(1, verdict, sort); }, [verdict, sort, i18n.language, fetchData]);

  const handlePage = (p) => {
    fetchData(p, verdict, sort);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="min-h-screen" data-testid="videos-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <div className="flex items-center gap-2 mb-2">
            <ScanEye className="w-4 h-4 text-brand-primary" />
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
              {t("videos.archive")}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
                {t("videos.title")}
              </h1>
              <p className="text-muted-foreground mt-2 text-sm">
                {t("videos.subtitle")}{" "}
                <span className="font-mono text-brand-primary">{total.toLocaleString()}</span> {t("videos.public")}.
              </p>
            </div>
            <Link to={user ? "/analyze" : "/register"}>
              <Button className="rounded-md bg-brand-primary hover:bg-brand-primary/90 text-white text-sm gap-2" data-testid="cta-analyze-btn">
                {t("videos.analyzeBtn")} <ExternalLink className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Sort Tabs */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.08 }}
          className="flex items-center gap-1 border-b border-border mb-1" data-testid="sort-tabs">
          {SORTS.map(({ key, labelKey }) => (
            <button
              key={key}
              onClick={() => setSort(key)}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-sm transition-colors border-b-2 -mb-px ${
                sort === key
                  ? "text-foreground border-brand-primary"
                  : "text-muted-foreground border-transparent hover:text-foreground hover:border-border"
              }`}
              data-testid={`sort-${key}`}
            >
              {t(labelKey)}
            </button>
          ))}
        </motion.div>

        {/* Verdict Filters */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="flex items-center gap-2 mt-4 mb-8 flex-wrap" data-testid="verdict-filters">
          <Filter className="w-3.5 h-3.5 text-muted-foreground" />
          {FILTERS.map(({ key, labelKey }) => (
            <button
              key={String(key)}
              onClick={() => setVerdict(key)}
              className={`px-3 py-1.5 rounded-sm text-xs font-mono transition-colors border ${
                verdict === key
                  ? "bg-brand-primary text-white border-brand-primary"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/20"
              }`}
              data-testid={`filter-${key || "all"}`}
            >
              {t(labelKey)}
            </button>
          ))}
        </motion.div>

        {/* Loading skeleton */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="rounded-md border border-border bg-card overflow-hidden animate-pulse">
                <div className="h-40 bg-secondary/40" />
                <div className="p-4 space-y-2">
                  <div className="h-3 bg-secondary/60 rounded w-3/4" />
                  <div className="h-3 bg-secondary/40 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && analyses.length === 0 && (
          <div className="text-center py-24 space-y-4" data-testid="videos-empty">
            <FileVideo className="w-12 h-12 text-muted-foreground/30 mx-auto" />
            <p className="font-heading text-xl font-semibold">{t("videos.empty.title")}</p>
            <p className="text-sm text-muted-foreground">{t("videos.empty.text")}</p>
            <Link to="/register">
              <Button className="rounded-md bg-brand-primary text-white mt-2">{t("videos.empty.cta")}</Button>
            </Link>
          </div>
        )}

        {/* Grid */}
        {!loading && analyses.length > 0 && (
          <AnimatePresence mode="wait">
            <motion.div
              key={`${verdict}-${sort}-${page}`}
              variants={container}
              initial="hidden"
              animate="show"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
              data-testid="videos-grid"
            >
              {analyses.map((analysis) => {
                const meta = VERDICT_META[analysis.verdict?.toUpperCase()] || VERDICT_META.UNCERTAIN;
                const Icon = meta.icon;
                const displayLabel = getDisplayLabel(analysis.verdict, analysis.confidence, t);
                const shareLink = analysis.share_id ? `/share/${analysis.share_id}` : `/share/${analysis.id}`;
                const locale = i18n.language === "id" ? "id-ID" : "en-US";
                const date = analysis.created_at
                  ? new Date(analysis.created_at).toLocaleDateString(locale, {
                      day: "numeric", month: "short", year: "numeric"
                    })
                  : "—";

                return (
                  <motion.div
                    key={analysis.id}
                    variants={item}
                    className="group rounded-md border border-border bg-card hover:border-brand-primary/30 transition-all overflow-hidden"
                    data-testid={`video-card-${analysis.id}`}
                  >
                    {/* Thumbnail */}
                    <Link to={shareLink} className="block relative overflow-hidden">
                      {analysis.thumbnail ? (
                        <div className="relative h-40 overflow-hidden bg-secondary/30">
                          <img
                            src={analysis.thumbnail}
                            alt={analysis.video_name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                          <div className={`absolute bottom-2 left-3 flex items-center gap-1.5 text-xs font-mono font-semibold px-2 py-0.5 rounded-sm border ${meta.color} ${meta.border} bg-black/60`}>
                            <Icon className="w-3 h-3" />
                            {displayLabel}
                          </div>
                          <span className={`absolute bottom-2 right-3 text-base font-heading font-bold ${meta.color}`}>
                            {Math.round(analysis.confidence)}%
                          </span>
                        </div>
                      ) : (
                        <div className={`relative h-40 flex items-center justify-center ${meta.bg} border-b ${meta.border}`}>
                          <div className="text-center">
                            <Icon className={`w-10 h-10 mx-auto mb-2 ${meta.color} opacity-60`} />
                            <div className={`text-xs font-mono font-bold ${meta.color}`}>{displayLabel}</div>
                            <div className={`text-lg font-heading font-bold ${meta.color}`}>
                              {Math.round(analysis.confidence)}%
                            </div>
                          </div>
                        </div>
                      )}
                    </Link>

                    {/* Card body */}
                    <div className="p-4">
                      <Link to={shareLink} className="block">
                        <h3 className="text-sm font-semibold line-clamp-1 group-hover:text-brand-primary transition-colors mb-0.5" data-testid={`video-title-${analysis.id}`}>
                          {analysis.content_title || analysis.video_name || "Untitled"}
                        </h3>
                        <p className="text-xs font-mono text-muted-foreground truncate mb-1" data-testid={`video-name-${analysis.id}`}>
                          {analysis.video_name}
                        </p>
                      </Link>
                      {/* Video source URL */}
                      {analysis.video_url && (
                        <a href={analysis.video_url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs font-mono text-brand-primary hover:underline truncate max-w-full mb-2"
                          title={analysis.video_url} onClick={(e) => e.stopPropagation()}>
                          <Link2 className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{analysis.video_url}</span>
                        </a>
                      )}
                      <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed mb-3">
                        {analysis.excerpt || analysis.summary}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-mono text-muted-foreground/70">{analysis.user_display_name || t("videos.userFallback")}</span>
                          <div className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {date}
                          </div>
                        </div>
                        <Link to={shareLink}>
                          <Button size="sm" variant="ghost" className="h-6 px-2 rounded-sm text-xs gap-1 text-brand-primary hover:bg-brand-primary/10">
                            {t("videos.card.view")} <ExternalLink className="w-3 h-3" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        )}

        {/* Pagination */}
        {!loading && pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10" data-testid="pagination">
            <Button
              variant="outline" size="sm"
              onClick={() => handlePage(page - 1)}
              disabled={page <= 1}
              className="rounded-sm text-xs"
            >
              &larr; {t("videos.prev")}
            </Button>
            <span className="text-xs font-mono text-muted-foreground px-2">
              {page} / {pages}
            </span>
            <Button
              variant="outline" size="sm"
              onClick={() => handlePage(page + 1)}
              disabled={page >= pages}
              className="rounded-sm text-xs"
            >
              {t("videos.next")} &rarr;
            </Button>
          </div>
        )}

        {/* CTA banner */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="mt-16 rounded-md border border-brand-primary/20 bg-brand-primary/5 p-8 text-center">
          <ScanEye className="w-8 h-8 text-brand-primary mx-auto mb-3" />
          <h2 className="font-heading text-2xl font-bold mb-2">{t("videos.cta.title")}</h2>
          <p className="text-muted-foreground text-sm mb-5 max-w-md mx-auto">
            {t("videos.cta.desc")}
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link to={user ? "/analyze" : "/register"}>
              <Button className="rounded-md bg-brand-primary hover:bg-brand-primary/90 text-white">{t("videos.cta.btn")}</Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" className="rounded-md">{t("nav.pricing")}</Button>
            </Link>
          </div>
        </motion.div>

      </div>
    </main>
  );
}
